import type { Session } from '@supabase/supabase-js';
import { db, META, type LocalHousehold } from '@/db/db';
import { BabyRecordSchema, EventRecordSchema, fromEventRecord, toBabyRecord, toEventRecord } from '@/lib/schemas';
import { getCloudClient } from './client';
import { recordFromWire, parseRetiredBabies } from '@/sync/protocol';
import { purgeBaby } from '@/privacy/purge';
import { enqueueOp } from '@/db/repo';
import type { BabyRecord, EventRecord } from '@/lib/schemas';

export interface AccountSession { userId: string; email: string | null }
export interface CloudHousehold { id: string; name: string; role: 'owner' | 'caregiver' }
const tables = () => [db.events, db.babies, db.outbox, db.meta, db.importBatches, db.predictionLog];
let generation = Date.now();
let transition: Promise<void> = Promise.resolve();
const cloudContextKey = (user: string, household: string) => `cloudContext:${user}:${household}`;
function workspaceMeta(key: string) {
  return key !== META.localContext && key !== META.deviceId && key !== META.session && key !== META.authGeneration
    && key !== 'syncLease' && key !== 'privacyBusy' && !key.startsWith('cloudContext:');
}

/** Fence pending switches, uploads and stale forms while an explicit erase runs. */
export async function privacyAction(expected: { householdId: string; userId: string | null },
  remote: (() => Promise<void>) | null, erase: () => Promise<void>, eraseDetached?: () => Promise<void>): Promise<void> {
  const epoch = ++generation;
  const marker = crypto.randomUUID();
  await transition.catch(() => {});
  await db.transaction('rw', tables(), async () => {
    check(epoch);
    const h = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
    const s = (await db.meta.get(META.session))?.value as AccountSession | undefined;
    if ((h?.id ?? '') !== expected.householdId || (s?.userId ?? null) !== expected.userId) throw new Error('Workspace changed. Reopen this action.');
    if (await db.meta.get('privacyBusy')) throw new Error('Another privacy action is running.');
    await db.meta.put({ key: 'privacyBusy', value: marker });
    await db.meta.put({ key: META.authGeneration, value: epoch });
    await db.meta.delete('syncLease');
  });
  try {
    await remote?.();
    const detached = await db.transaction('rw', tables(), async () => {
      if (generation !== epoch || (await db.meta.get('privacyBusy'))?.value !== marker || (await db.meta.get(META.authGeneration))?.value !== epoch) {
        // The explicitly requested server erasure succeeded. Remove only its
        // matching archived copies, without touching a replacement workspace.
        await eraseDetached?.(); return true;
      }
      await erase();
      return false;
    });
    if (detached) throw new Error('Account changed. Server deletion completed; matching cached copies were erased. Reopen the current workspace.');
  } finally {
    await db.transaction('rw', db.meta, async () => {
      if ((await db.meta.get('privacyBusy'))?.value === marker) await db.meta.delete('privacyBusy');
    });
  }
}

/** Called inside the privacy transaction; deliberately does not archive erased work. */
export async function discardCloudWorkspace() {
  await restoreLocal();
}
function online() { if (!navigator.onLine) throw new Error('Connect to the internet to validate household access.'); }
function check(epoch: number) { if (generation !== epoch) throw new Error('Account changed. Try again.'); }
async function context() {
  const session = (await db.meta.get(META.session))?.value as AccountSession | undefined;
  if (!session) throw new Error('Sign in first.');
  return session;
}
async function captureLocal() {
  const household = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
  if (!household || household.cloud) return;
  const value = { household, babies: await db.babies.toArray(), events: await db.events.toArray(), outbox: await db.outbox.toArray(),
    imports: await db.importBatches.toArray(), predictions: await db.predictionLog.toArray(),
    metadata: (await db.meta.toArray()).filter(r => workspaceMeta(r.key) && !r.key.startsWith(META.syncCursorPrefix) && !r.key.startsWith('syncState:')),
    activeBabyId: (await db.meta.get(META.activeBabyId))?.value, settings: (await db.meta.get(META.settings))?.value };
  await db.meta.put({ key: META.localContext, value });
}
/** Retain unsent work under its validated account; never render it for another. */
async function captureCloud() {
  const household = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
  if (!household?.cloud || !household.accountId) return;
  const key = cloudContextKey(household.accountId, household.id);
  if (!(await db.outbox.where('household_id').equals(household.id).count())) { await db.meta.delete(key); return; }
  const value = { household, babies: await db.babies.toArray(), events: await db.events.toArray(), outbox: await db.outbox.toArray(),
    imports: await db.importBatches.toArray(), predictions: await db.predictionLog.toArray(),
    metadata: (await db.meta.toArray()).filter(row => workspaceMeta(row.key)) };
  await db.meta.put({ key, value });
}
async function clearWorkspace() {
  await Promise.all([db.events.clear(), db.babies.clear(), db.outbox.clear(), db.importBatches.clear(), db.predictionLog.clear()]);
  const rows = await db.meta.toArray();
  await db.meta.bulkDelete(rows.filter(r => r.key !== META.localContext && r.key !== META.deviceId && r.key !== META.session && r.key !== META.authGeneration && !r.key.startsWith('cloudContext:')).map(r => r.key));
}
async function restoreLocal() {
  const saved = (await db.meta.get(META.localContext))?.value as Awaited<ReturnType<typeof localSnapshot>> | undefined;
  await clearWorkspace();
  if (!saved) return;
  await db.babies.bulkPut(saved.babies); await db.events.bulkPut(saved.events); await db.outbox.bulkPut(saved.outbox);
  await db.importBatches.bulkPut(saved.imports); await db.predictionLog.bulkPut(saved.predictions);
  if (saved.metadata) await db.meta.bulkPut(saved.metadata);
  await db.meta.put({ key: META.household, value: saved.household });
  if (saved.activeBabyId) await db.meta.put({ key: META.activeBabyId, value: saved.activeBabyId });
  if (saved.settings) await db.meta.put({ key: META.settings, value: saved.settings });
}
// Inferred archive type without coupling metadata to the schema migration.
async function localSnapshot() {
  return { household: {} as LocalHousehold, babies: await db.babies.toArray(), events: await db.events.toArray(), outbox: await db.outbox.toArray(), imports: await db.importBatches.toArray(), predictions: await db.predictionLog.toArray(), metadata: await db.meta.toArray(), activeBabyId: undefined as unknown, settings: undefined as unknown };
}

/** Token snapshots never authorize cloud operations; every switch validates online membership. */
export async function applyAccountSession(session: AccountSession | null): Promise<void> {
  const epoch = ++generation;
  const operation = transition.catch(() => {}).then(() => db.transaction('rw', tables(), async () => {
    check(epoch);
    await db.meta.delete('privacyBusy');
    const prior = (await db.meta.get(META.session))?.value as AccountSession | undefined;
    const active = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
    if (active && !active.cloud) await captureLocal();
    if (active?.cloud && (prior?.userId !== session?.userId || !session)) { await captureCloud(); await restoreLocal(); }
    if (session) await db.meta.put({ key: META.session, value: session }); else await db.meta.delete(META.session);
    await db.meta.put({ key: META.authGeneration, value: epoch });
    check(epoch);
  }));
  transition = operation;
  await operation;
}

export function initializeAuth(onError: (message: string) => void = () => {}, onReady: (generation: number) => void = () => {}): () => void {
  let alive = true;
  let revision = 0;
  let initial = true;
  const client = getCloudClient();
  const update = async (session: Session | null) => {
    const attempt = ++revision;
    let expectedGeneration: number | undefined;
    try {
      // Hide persisted caches at startup/account changes. Same-account token refresh
      // keeps the selected snapshot while the server validates the refreshed token.
      const prior = (await db.meta.get(META.session))?.value as AccountSession | undefined;
      const previousHousehold = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
      const resumeCloud = initial && previousHousehold?.cloud && previousHousehold.accountId === prior?.userId ? previousHousehold.id : undefined;
      if (!alive || revision !== attempt) return;
      // A browser restart abandons an interrupted request. Server retirement
      // guards determine its outcome; never leave offline local editing locked.
      if (initial && await db.meta.get('privacyBusy')) {
        await db.transaction('rw', db.meta, async () => {
          await db.meta.delete('privacyBusy');
          await db.meta.put({ key: META.authGeneration, value: ++generation });
        });
      }
      // Offline reload may use only the previously validated account's cached
      // workspace. Server access is revalidated when connectivity returns.
      if (!navigator.onLine && session?.user?.id && prior?.userId === session.user.id) {
        initial = false; onReady((await db.meta.get(META.authGeneration))?.value as number ?? 0); return;
      }
      if (initial || !session || prior?.userId !== session.user.id) await applyAccountSession(null);
      initial = false;
      if (alive) onReady(generation);
      if (!session) return;
      const epoch = generation;
      expectedGeneration = epoch;
      const { data, error } = await client.auth.getUser(session.access_token);
      if (!alive || revision !== attempt) return;
      check(epoch);
      if (error) throw error;
      await applyAccountSession({ userId: data.user.id, email: data.user.email ?? null });
      if (resumeCloud && data.user.id === prior?.userId) await switchHousehold(resumeCloud);
      if (alive && revision === attempt) onReady(generation);
    } catch (e) { if (alive && revision === attempt && (expectedGeneration === undefined || expectedGeneration === generation)) { await applyAccountSession(null); onReady(generation); onError(e instanceof Error ? e.message : 'Unable to restore sign-in.'); } }
  };
  const { data } = client.auth.onAuthStateChange((_event, session) => { queueMicrotask(() => { if (alive) void update(session); }); });
  void client.auth.getSession().then(({ data, error }) => { if (!alive || revision) return; if (error) { onError(error.message); void update(null); } else void update(data.session); }).catch(e => { if (alive) { onError(String(e)); void update(null); } });
  let validatingMembership = false;
  const revalidate = async () => {
    if (!alive || validatingMembership || !navigator.onLine || document.visibilityState === 'hidden') return;
    validatingMembership = true;
    const epoch = generation;
    try {
      const active = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
      if (!alive || epoch !== generation || !active?.cloud) return;
      const rows = await listHouseholds();
      if (!alive || epoch !== generation) return;
      if (!rows.some(row => row.id === active.id)) {
        await returnToLocalHousehold();
        onError('Household access was revoked. Local tracking has been restored.');
      }
    } catch (e) {
      if (alive && epoch === generation) {
        await returnToLocalHousehold();
        onError(`Cloud access could not be validated. Local tracking has been restored. ${e instanceof Error ? e.message : ''}`);
      }
    } finally { validatingMembership = false; }
  };
  const onForeground = () => { void revalidate(); };
  window.addEventListener('online', onForeground); window.addEventListener('focus', onForeground); document.addEventListener('visibilitychange', onForeground);
  return () => { alive = false; revision++; data.subscription.unsubscribe(); window.removeEventListener('online', onForeground); window.removeEventListener('focus', onForeground); document.removeEventListener('visibilitychange', onForeground); };
}

async function rpc<T>(name: string, args?: Record<string, unknown>): Promise<T> {
  online(); const { data, error } = await getCloudClient().rpc(name, args);
  if (error) throw error; return data as T;
}
export async function listHouseholds(): Promise<CloudHousehold[]> {
  const epoch = generation; await context();
  const rows = await rpc<CloudHousehold[]>('list_my_households'); check(epoch); return rows;
}
export async function switchHousehold(id: string): Promise<void> {
  online(); const epoch = ++generation; const account = await context();
  const households = await listHouseholds();
  const household = households.find(h => h.id === id);
  if (!household) throw new Error('Household access is no longer available.');
  // A single SQL snapshot avoids PostgREST max_rows truncation and inter-query skew.
  const snapshot = await rpc<{ babies: unknown[]; events: unknown[]; retired_baby_ids?: unknown } | null>('get_household_snapshot', { household_id: id });
  if (!snapshot || !Array.isArray(snapshot.babies) || !Array.isArray(snapshot.events)) throw new Error('Household snapshot is unavailable.');
  const babies = snapshot.babies.map(r => recordFromWire('baby', r, id) as ReturnType<typeof BabyRecordSchema.parse>);
  const events = snapshot.events.map(r => recordFromWire('event', r, id) as ReturnType<typeof EventRecordSchema.parse>);
  const retired = parseRetiredBabies(snapshot.retired_baby_ids);
  if (babies.some(b => b.household_id !== id) || events.some(e => e.household_id !== id || !babies.some(b => b.id === e.baby_id))) throw new Error('Invalid household snapshot.');
  const revalidated = (await listHouseholds()).find(h => h.id === id);
  if (!revalidated) throw new Error('Household access is no longer available.');
  await db.transaction('rw', tables(), async () => {
    check(epoch); if ((await context()).userId !== account.userId) throw new Error('Account changed.');
    await captureLocal(); await captureCloud();
    const archive = (await db.meta.get(cloudContextKey(account.userId, id)))?.value as Awaited<ReturnType<typeof localSnapshot>> | undefined;
    await clearWorkspace();
    if (archive && archive.household.id === id && archive.household.accountId === account.userId) {
      await db.babies.bulkPut(archive.babies); await db.events.bulkPut(archive.events); await db.outbox.bulkPut(archive.outbox);
      await db.importBatches.bulkPut(archive.imports); await db.predictionLog.bulkPut(archive.predictions);
      if (archive.metadata) await db.meta.bulkPut(archive.metadata.filter(row => workspaceMeta(row.key)));
    } else {
      await db.babies.bulkPut(babies.map(b => ({ ...b, sync_state: 'synced' as const })));
      await db.events.bulkPut(events.map(e => fromEventRecord(e, 'synced')));
    }
    await db.meta.put({ key: META.household, value: { ...revalidated, cloud: true, accountId: account.userId, syncReady: false } });
    for (const babyId of retired) await purgeBaby(db, id, babyId, true);
    check(epoch);
  });
}
export async function returnToLocalHousehold() {
  ++generation;
  await db.transaction('rw', tables(), async () => { const active = (await db.meta.get(META.household))?.value as LocalHousehold | undefined; if (active?.cloud) { await captureCloud(); await restoreLocal(); } });
}
export async function signOut() {
  await applyAccountSession(null);
  const { error } = await getCloudClient().auth.signOut({ scope: 'local' }); if (error) throw error;
}
export async function signIn(email: string, password: string) {
  online(); const { error } = await getCloudClient().auth.signInWithPassword({ email, password }); if (error) throw error;
}
export async function signUp(email: string, password: string) {
  online(); const { error } = await getCloudClient().auth.signUp({ email, password, options: { emailRedirectTo: window.location.origin + window.location.pathname } }); if (error) throw error;
}
export async function createHousehold(name: string) {
  const epoch = generation; await context(); const result = await rpc<CloudHousehold>('create_household', { name: name.trim() }); check(epoch); return result;
}
export async function inviteMember(householdId: string, email: string) {
  const epoch = generation; const rows = await listHouseholds();
  if (rows.find(h => h.id === householdId)?.role !== 'owner') throw new Error('Only owners can invite caregivers.');
  const token = await rpc<string>('invite_household_member', { household_id: householdId, email }); check(epoch); return token;
}
export async function acceptInvite(token: string) {
  const epoch = generation; await context(); const result = await rpc<CloudHousehold>('accept_household_invite', { token: token.trim() }); check(epoch); return result;
}
export async function listMembers(householdId: string) {
  const epoch = generation; await listHouseholds();
  const members: {user_id: string; role: string}[] = [];
  for (let offset = 0; offset < 100_000; offset += 1000) {
    const { data, error } = await getCloudClient().from('household_members').select('user_id,role').eq('household_id', householdId).order('user_id').range(offset, offset + 999);
    if (error) throw error; check(epoch);
    members.push(...(data ?? [])); if (!data || data.length < 1000) return members;
  }
  throw new Error('Too many household members to display safely.');
}
export async function removeMember(householdId: string, userId: string) {
  const epoch = generation; const rows = await listHouseholds();
  if (rows.find(h => h.id === householdId)?.role !== 'owner') throw new Error('Only owners can remove caregivers.');
  await rpc('remove_household_member', { household_id: householdId, user_id: userId }); check(epoch);
}
/** Explicit snapshot upload; local workspace remains archived and can be recovered on logout. */
export async function promoteLocalHousehold(confirmed: boolean) {
  if (!confirmed) throw new Error('Confirm the snapshot upload first.');
  const epoch = generation; const account = await context();
  const request = await db.transaction('rw', tables(), async () => {
    check(epoch);
    const household = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
    if (!household || household.cloud) throw new Error('Select the local household first.');
    const key = `promotionRequest:${account.userId}:${household.id}`;
    const existing = (await db.meta.get(key))?.value as { household: {id: string; name: string}; babies: ReturnType<typeof toBabyRecord>[]; events: ReturnType<typeof toEventRecord>[] } | undefined;
    if (existing) return { key, snapshot: existing };
    const snapshot = { household: { id: household.id, name: household.name }, babies: (await db.babies.orderBy('id').toArray()).map(toBabyRecord), events: (await db.events.orderBy('id').toArray()).map(toEventRecord) };
    await db.meta.put({ key, value: snapshot });
    return { key, snapshot };
  });
  // Persist exact payload before network: response loss can follow a successful server commit.
  // Retries always send that original snapshot, even if local recording has continued.
  let result: CloudHousehold;
  try { result = await rpc<CloudHousehold>('promote_local_household', request.snapshot); }
  catch (error) {
    if ((error as { code?: string })?.code === 'KR001') throw new Error('This local copy contains permanently deleted cloud identifiers and cannot be uploaded unchanged. Keep it as a local backup, or create a new household and profiles for future records.');
    throw error;
  }
  check(epoch); if ((await context()).userId !== account.userId) throw new Error('Account changed.');
  // Keep active local state/outbox intact. Switching separately fetches acknowledged server records.
  return result;
}

function content(record: BabyRecord | EventRecord) {
  const { version: _version, created_at: _created, updated_at: _updated, ...fields } = record;
  void _version; void _created; void _updated;
  if ('created_by' in fields) { delete (fields as Partial<EventRecord>).created_by; delete (fields as Partial<EventRecord>).updated_by; }
  const stable = (value: unknown): unknown => Array.isArray(value) ? value.map(stable) : value && typeof value === 'object'
    ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => [k, stable(v)])) : value;
  return JSON.stringify(stable(fields));
}
/** Explicit migration to cloud writes, using only the exact acknowledged baseline. */
export async function adoptLocalHouseholdForSync(confirmed: boolean) {
  const epoch = generation; const account = await context();
  const result = await promoteLocalHousehold(confirmed);
  check(epoch);
  const key = `promotionRequest:${account.userId}:${result.id}`;
  const baseline = (await db.meta.get(key))?.value as { babies: BabyRecord[]; events: EventRecord[] } | undefined;
  if (!baseline) throw new Error('The acknowledged upload snapshot is unavailable.');
  const snapshot = await rpc<{ babies: unknown[]; events: unknown[] }>('get_household_snapshot', { household_id: result.id });
  const remoteBabies = snapshot.babies.map(r => recordFromWire('baby', r, result.id) as BabyRecord);
  const remoteEvents = snapshot.events.map(r => recordFromWire('event', r, result.id) as EventRecord);
  check(epoch);
  await db.transaction('rw', tables(), async () => {
    check(epoch);
    const active = (await db.meta.get(META.household))?.value as LocalHousehold | undefined;
    if (!active || active.cloud || active.id !== result.id || (await context()).userId !== account.userId) throw new Error('Household/account changed.');
    const ops = await db.outbox.toArray();
    if (ops.some(op => op.attempted)) throw new Error('An existing upload attempt must be resolved before adopting this workspace.');
    const currentBabies = (await db.babies.toArray()).map(toBabyRecord);
    const currentEvents = (await db.events.toArray()).map(toEventRecord);
    await captureLocal();
    await db.outbox.clear(); await db.events.clear(); await db.babies.clear();
    const migrate = async (entity: 'baby' | 'event', original: (BabyRecord | EventRecord)[], current: (BabyRecord | EventRecord)[], remote: (BabyRecord | EventRecord)[]) => {
      const baselineMap = new Map(original.map(r => [r.id, recordFromWire(entity, { ...r, version: 1 }, result.id)]));
      const currentMap = new Map(current.map(r => [r.id, r])); const remoteMap = new Map(remote.map(r => [r.id, r]));
      for (const base of baselineMap.values()) {
        const server = remoteMap.get(base.id);
        if (!server || (server.version === 1 && content(server) !== content(base))) throw new Error('Server adoption baseline could not be verified.');
      }
      const ids = new Set([...baselineMap.keys(), ...currentMap.keys(), ...remoteMap.keys()]);
      for (const id of ids) {
        const base = baselineMap.get(id); const local = currentMap.get(id); const server = remoteMap.get(id);
        const normalizedLocal = local && recordFromWire(entity, { ...local, version: Math.max(1, local.version) }, result.id);
        let record: BabyRecord | EventRecord | undefined; let action: 'create' | 'update' | 'delete' | undefined; let version = 0;
        if (base) {
          if (normalizedLocal && content(normalizedLocal) === content(base)) record = server;
          else {
            // Preserve later edits against version 1, so a concurrent remote edit
            // produces CAS conflict instead of being silently overwritten.
            version = 1;
            record = { ...(normalizedLocal ?? base), version: 1, created_at: server!.created_at,
              ...('created_by' in server! ? { created_by: (server as EventRecord).created_by } : {}),
              deleted_at: normalizedLocal?.deleted_at ?? (local ? null : new Date().toISOString()) };
            action = record.deleted_at ? 'delete' : 'update';
          }
        } else if (local) { record = { ...local, version: 0 }; action = local.deleted_at ? undefined : 'create'; }
        else record = server;
        if (!record) continue;
        if (entity === 'event') await db.events.put(fromEventRecord(record as EventRecord, action ? 'local' : 'synced'));
        else await db.babies.put({ ...record as BabyRecord, sync_state: action ? 'local' : 'synced' });
        if (action) await enqueueOp({ household_id: result.id, entity, entity_id: id, op: action, data: record, currentServerVersion: version });
      }
    };
    await migrate('baby', baseline.babies, currentBabies, remoteBabies);
    await migrate('event', baseline.events, currentEvents, remoteEvents);
    await db.meta.put({ key: META.household, value: { ...result, cloud: true, accountId: account.userId, syncReady: false } });
    await db.meta.delete(key);
    check(epoch);
  });
  return result;
}
